import React, { Fragment } from "react";

import img13 from "../../../images/service/E-Commerce Blockchain Development/1.png";
import img14 from "../../../images/service/E-Commerce Blockchain Development/2.png";
import img15 from "../../../images/service/E-Commerce Blockchain Development/3.png";

// Service icons
import sIcon1 from "../../../images/icon/service-icon01.svg";
import sIcon2 from "../../../images/icon/service-icon02.svg";
import sIcon3 from "../../../images/icon/service-icon03.svg";
import sIcon4 from "../../../images/icon/service-icon04.svg";
import sIcon5 from "../../../images/icon/service-icon05.svg";
import sIcon6 from "../../../images/icon/service-icon06.svg";

// Common Components
import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Scrollbar from "../../../components/scrollbar/scrollbar";
import Footer from "../../../components/footer/Footer";

import HeroSection from "../../../components/PageSection/HeroSection";
import ServiceCardsSection from "../../../components/PageSection/ServiceCardSection";
import Benefit from "../../../components/PageSection/BenefitSection";
import KeyFeaturesSection from "../../../components/PageSection/KeyFeature";
import WhyChooseUsSection from "../../../components/PageSection/WhyChooseUsSection";
import FaqSection from "../../../components/PageSection/FAQsection";
import FinalCenteredCTA from "../../../components/PageSection/FinalCTA";
import Industries from "../../../components/Industries/Industries";
import TheorySection from "../../../components/PageSection/Theory";

// =====================
// DATA
// =====================

const serviceCards = [
  {
    icon: sIcon1,
    title: "Decentralized Marketplace Platforms",
    content:
      "Design and build blockchain-powered marketplaces that remove intermediaries and improve transaction transparency.",
  },
  {
    icon: sIcon2,
    title: "Smart Contract Integration",
    content:
      "Implement self-executing smart contracts to automate orders, payments, and supply chain tasks.",
  },
  {
    icon: sIcon3,
    title: "Secure Payment Solutions",
    content:
      "Enable crypto payment options and secure wallets to reduce processing fees and accelerate settlement.",
  },
];

const benefits = [
  {
    title: "Faster Settlement",
    description:
      "Blockchain’s peer-to-peer model reduces delays by eliminating reliance on middlemen and streamlining transaction flows.",
    icon: sIcon1,
  },
  {
    title: "Lower Processing Costs",
    description:
      "Use decentralized payments to cut out third-party fees and improve profitability for merchants and buyers alike.",
    icon: sIcon2,
  },
  {
    title: "Transparent Operations",
    description:
      "Immutable ledger entries allow stakeholders to track payments, orders, and product history in real time.",
    icon: sIcon3,
  },
  {
    title: "Enhanced Data Security",
    description:
      "Blockchain encryption protects sensitive personal and financial information from unauthorized access.",
    icon: sIcon4,
  },
];

const faqs = [
  {
    q: "What is ecommerce blockchain development?",
    a: "Ecommerce blockchain development refers to building decentralized systems that improve marketplace transparency, security, and efficiency using distributed ledger technology. :contentReference[oaicite:1]{index=1}",
  },
  {
    q: "How does blockchain benefit online marketplaces?",
    a: "Blockchain can reduce transaction costs, enhance data protection, and automate processes like payments and supply chain tracking.",
  },
  {
    q: "Can blockchain reduce payment processing fees?",
    a: "Yes—by allowing direct transfers without intermediaries, blockchain payments can significantly lower traditional processing charges. :contentReference[oaicite:2]{index=2}",
  },
  {
    q: "Is blockchain secure for ecommerce data?",
    a: "Blockchain’s decentralized structure and encryption greatly improve security for user and transaction data compared with traditional systems.",
  },
];

// =====================
// PAGE COMPONENT
// =====================

export default function EcommerceBlockchainDevelopment() {
  return (
    <Fragment>
      <Header />

      <PageTitle
        pageTitle="E-Commerce Blockchain Development"
        pagesub="Secure, transparent and scalable blockchain solutions for online commerce"
      />

      {/* HERO SECTION */}
      <HeroSection
        title={
          <>
            Transform Your{" "}
            <span className="gradient-text">E-Commerce Business</span>{" "}
            with Blockchain
          </>
        }
        subtitle={`Harness the power of decentralized technology to enhance customer trust, improve security, 
and optimize payment and logistics workflows across your e-commerce ecosystem.`}
        buttonText="Book Consultation"
        buttonLink="/contact"
        image={img13}
        imageAlt="E-Commerce Blockchain Development"
      />

      {/* ================= THEORY 1 ================= */}
      <TheorySection
        title="Why Blockchain for E-Commerce?"
        image={img14}
        paragraphs={[
          "Integrating blockchain into ecommerce platforms unlocks new value by enabling safer, more transparent transaction flows for buyers and sellers alike. :contentReference[oaicite:3]{index=3}",
          "Blockchain simplifies the interaction of stakeholders, enhances traceability, and eliminates many inefficiencies found in traditional ecommerce systems. :contentReference[oaicite:4]{index=4}",
        ]}
      />

      {/* SERVICES */}
      <ServiceCardsSection
        title="Core E-Commerce Blockchain Services"
        cards={serviceCards}
      />

      {/* ================= THEORY 2 ================= */}
      <TheorySection
        title="Challenges in Traditional E-Commerce"
        image={img15}
        paragraphs={[
          "Conventional ecommerce systems often struggle with high competition, rising transaction fees, data security gaps, and complex management of logistics and payments. :contentReference[oaicite:5]{index=5}",
          "Blockchain technology tackles these issues by decentralizing operations, reducing reliance on third parties, and safeguarding sensitive data from unauthorized access. :contentReference[oaicite:6]{index=6}",
        ]}
      />

      {/* BENEFITS */}
      <Benefit
        title="Benefits of Blockchain in E-Commerce"
        benefits={benefits}
      />

      {/* KEY FEATURES */}
      <KeyFeaturesSection
        title="Key Features of Our E-Commerce Blockchain Solutions"
        subtitle="Enhance marketplace experience with performance and security at the core"
        features={[
          {
            icon: sIcon1,
            title: "Peer-to-Peer Transactions",
            description:
              "Eliminate intermediaries and reduce friction in payment processing.",
          },
          {
            icon: sIcon2,
            title: "Immutable Ledger Entries",
            description:
              "Track orders and payments reliably with permanent records.",
          },
          {
            icon: sIcon3,
            title: "Tokenized Payments",
            description:
              "Accept cryptocurrency and token-based payments for global reach.",
          },
          {
            icon: sIcon4,
            title: "Enhanced Encryption",
            description:
              "Protect sensitive user and transaction data with cryptographic safeguards.",
          },
          {
            icon: sIcon5,
            title: "Smart Contract Automation",
            description:
              "Automate order fulfillment, escrow, and refunds with pre-defined logic.",
          },
          {
            icon: sIcon6,
            title: "Real-Time Visibility",
            description:
              "Provide transparent status updates for every transaction stage.",
          },
        ]}
      />

      <Industries />

      {/* WHY CHOOSE US */}
      <WhyChooseUsSection
        items={[
          "Specialists in blockchain for ecommerce transformation",
          "Customized solutions for business growth",
          "Comprehensive support from strategy to deployment",
        ]}
      />

      {/* FAQ */}
      <FaqSection faqs={faqs} />

      {/* FINAL CTA */}
      <FinalCenteredCTA />

      <Footer />
      <Scrollbar />
    </Fragment>
  );
}
